import {
  NetworkError,
  type HttpBody,
  type HttpRequest,
  type HttpResponse,
  type TransportMode,
} from "./types.ts";

export type Fetcher = typeof fetch;
export const MAX_FETCH_BYTES = 16 * 1024 * 1024;

export function filteredBrowserHeaders(input?: Record<string, string>): Headers {
  const headers = new Headers(input);
  for (const name of [...headers.keys()]) {
    if (
      /^(?:referer|origin|user-agent|host|cookie|connection|content-length|accept-encoding|access-control-request-.+|proxy-.+|sec-.+)$/i.test(
        name,
      )
    )
      headers.delete(name);
  }
  return headers;
}

export function responseIsChallenge(headers: Headers, text: string): boolean {
  return (
    headers.get("cf-mitigated") === "challenge" ||
/id\s*=\s*["']challenge-form["']|<title[^>]*>\s*(?:Just a moment|Attention Required)|cf-chl-/i.test(
      text.slice(0, 65536),
    )
  );
}

export async function readLimitedBody(
  response: Response,
  signal: AbortSignal,
): Promise<Uint8Array> {
  if (Number(response.headers.get("content-length")) > MAX_FETCH_BYTES) {
    await response.body?.cancel();
    throw new NetworkError("INVALID_RESPONSE", "La respuesta supera 16 MiB.");
  }
  if (!response.body) return new Uint8Array();
  const reader = response.body.getReader();
  const chunks: Uint8Array[] = [];
  let size = 0;
  const abort = () => {
    void reader.cancel().catch(() => undefined);
  };
  signal.addEventListener("abort", abort, { once: true });
  try {
    signal.throwIfAborted();
    while (true) {
      const { done, value } = await reader.read();
      signal.throwIfAborted();
      if (done) break;
      size += value.byteLength;
      if (size > MAX_FETCH_BYTES)
        throw new NetworkError("INVALID_RESPONSE", "La respuesta supera 16 MiB.");
      chunks.push(value);
    }
    const result = new Uint8Array(size);
    let offset = 0;
    for (const chunk of chunks) {
      result.set(chunk, offset);
      offset += chunk.byteLength;
    }
    return result;
  } catch (error) {
    await reader.cancel().catch(() => undefined);
    throw error;
  } finally {
    signal.removeEventListener("abort", abort);
    reader.releaseLock();
  }
}

export async function executeFetch<T extends HttpBody>(
  request: HttpRequest,
  mode: TransportMode,
  fetcher: Fetcher,
  endpoint: string,
  init: RequestInit,
  decode: (bytes: Uint8Array) => T,
  inspect?: (response: Response, bytes: Uint8Array) => void,
  finalUrl?: (response: Response) => string,
  onHeaders?: (response: Response) => void,
): Promise<HttpResponse<T>> {
  const timeoutMs = request.timeoutMs ?? 25000;
  if (!Number.isFinite(timeoutMs) || timeoutMs < 1 || timeoutMs > 28000)
    throw new NetworkError("INVALID_RESPONSE", "Timeout inválido.");
  if (request.signal?.aborted)
    throw new NetworkError("ABORTED", "Solicitud cancelada.", undefined, mode);
  const controller = new AbortController();
  let timedOut = false;
  const abort = () => controller.abort();
  request.signal?.addEventListener("abort", abort, { once: true });
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);
  try {
    if (request.signal?.aborted) controller.abort();
    const response = await fetcher(endpoint, { ...init, signal: controller.signal });
    onHeaders?.(response);
    if (response.type === "opaque" || response.status === 0)
      throw new NetworkError(
        "INVALID_RESPONSE",
        "Respuesta inaccesible por CORS.",
        undefined,
        mode,
      );
    const bytes = await readLimitedBody(response, controller.signal);
    inspect?.(response, bytes);
    if (responseIsChallenge(response.headers, new TextDecoder().decode(bytes.subarray(0, 65536))))
      throw new NetworkError(
        "CHALLENGE_REQUIRED",
        "Abrí la fuente, completá la verificación y reintentá.",
        response.status,
        mode,
      );
    return {
      status: response.status,
      finalUrl: finalUrl?.(response) ?? response.url ?? request.url,
      headers: Object.fromEntries(response.headers),
      body: decode(bytes),
      transport: mode,
      fromCache: false,
      classification: "content",
    };
  } catch (error) {
    if (controller.signal.aborted)
      throw new NetworkError(
        timedOut ? "TIMEOUT" : "ABORTED",
        timedOut ? "La fuente tardó demasiado en responder." : "Solicitud cancelada.",
        undefined,
        mode,
      );
    if (error instanceof NetworkError) throw error;
    throw new NetworkError(
      "HTTP_ERROR",
      error instanceof Error ? error.message : "No se pudo consultar la fuente.",
      undefined,
      mode,
    );
  } finally {
    clearTimeout(timer);
    request.signal?.removeEventListener("abort", abort);
  }
}
